/** Gateway との通信。認証はセッショントークン（Bearer）。会話は SSE。 */
import type { ReplyDeliveryReport } from "./delivery.js";
export const GATEWAY =
  (import.meta.env.VITE_GATEWAY_URL as string | undefined)?.replace(/\/$/, "") ?? "";

const TOKEN_KEY = "jarvis.session_token";
const DEVICE_KEY = "jarvis.device_id";
const CONV_KEY = "jarvis.conversation_id";

export const store = {
  get token() {
    return safeGet(TOKEN_KEY);
  },
  set token(v: string | null) {
    safeSet(TOKEN_KEY, v);
  },
  get deviceId() {
    return safeGet(DEVICE_KEY);
  },
  set deviceId(v: string | null) {
    safeSet(DEVICE_KEY, v);
  },
  get conversationId(): string {
    return safeGet(CONV_KEY) ?? "main";
  },
  set conversationId(v: string) {
    safeSet(CONV_KEY, v);
  },
};

function safeGet(k: string) {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k: string, v: string | null) {
  try {
    v === null ? localStorage.removeItem(k) : localStorage.setItem(k, v);
  } catch {
    /* private mode */
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(`${status} ${code}`);
  }
}

export async function api<T>(
  path: string,
  body?: unknown,
  method = body === undefined ? "GET" : "POST",
): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (store.token) headers.Authorization = `Bearer ${store.token}`;
  const res = await fetch(`${GATEWAY}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, j.error ?? "error");
  }
  return (await res.json()) as T;
}

export interface ChatFinal {
  trace_id: string;
  status: "ok" | "error" | "blocked";
  reply: string;
  cost_micro: number;
  reason?: string;
  /** この返事の途中でできた承認待ち（ADR-0030）。返事のすぐ下にカードを出す */
  approval_ids?: string[];
}

/**
 * POST /v1/chat を SSE で読む。partial は累積テキスト。
 * onStart は最初の知らせ: 返事の trace_id と読み上げ用の読み（無ければ null。ADR-0041）。読み上げの記録と中継に使う。
 * onRetract は流した文の取り消し（ツールの呼び出しが始まった・答え直す）。画面を消し、読み上げを止めて読み直す。
 * opts.reply_delivery は割り込みの報告（前の返事の途中で送ったとき。ADR-0042）。opts.signal で接続を切る
 * （前の返事は、次の発言の start を受けてから切る。切ったら AbortError で終わる）
 */
export async function chat(
  text: string,
  onPartial: (t: string) => void,
  onStart?: (trace_id: string, readings: Record<string, string> | null) => void,
  onRetract?: () => void,
  opts: { signal?: AbortSignal; reply_delivery?: ReplyDeliveryReport } = {},
): Promise<ChatFinal> {
  const res = await fetch(`${GATEWAY}/v1/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${store.token ?? ""}` },
    body: JSON.stringify({
      conversation_id: store.conversationId,
      text,
      ...(opts.reply_delivery ? { reply_delivery: opts.reply_delivery } : {}),
    }),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  if (!res.ok || !res.body) throw new ApiError(res.status, "chat_failed");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let final: ChatFinal | null = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx = buf.indexOf("\n\n");
    while (idx >= 0) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const ev = parseFrame(frame);
      if (ev?.event === "start") {
        const d = ev.data as { trace_id?: unknown; readings?: unknown };
        onStart?.(String(d.trace_id ?? ""), readingsOf(d.readings));
      }
      if (ev?.event === "partial") onPartial((ev.data as { text: string }).text);
      if (ev?.event === "retract") onRetract?.();
      if (ev?.event === "final") final = ev.data as ChatFinal;
      idx = buf.indexOf("\n\n");
    }
  }
  if (!final) throw new ApiError(0, "stream_ended_without_final");
  return final;
}

/** 読みの形（表記 → かな）だけ残す。無ければ null（Gateway が事実を読めなかった・古い版） */
export function readingsOf(v: unknown): Record<string, string> | null {
  if (!v || typeof v !== "object") return null;
  return Object.fromEntries(
    Object.entries(v).filter((e): e is [string, string] => typeof e[1] === "string"),
  );
}

export function parseFrame(frame: string): { event: string; data: unknown } | null {
  let event = "message";
  const data: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).trim());
  }
  if (!data.length) return null;
  try {
    return { event, data: JSON.parse(data.join("\n")) };
  } catch {
    return null;
  }
}
